import { AnyAction } from 'redux';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import * as navigate from 'app/area/shared/navigation';

import { mapSavedReportsValidation } from 'app/area/shared/saved-reports/utils/map-saved-reports-validation';
import { api as commonApi } from 'app/area/shared/saved-reports/api';
import { actionCreators as commonActionCreators } from 'app/area/shared/saved-reports/actions';
import { mapSavedReportsWarnings } from 'app/area/shared/saved-reports/utils/map-saved-reports-warnings';

import { endpoints } from '../common/endpoints';
import { mapSavedReportsFromWire, mapSavedReportsToWire } from '../common/map-saved-reports';
import { normalizeSequence } from '../common/normalize-sequence';
import { getBackRoute } from '../common/route';
import { SavedReportDashboard } from '../common/types-store';
import { dashboardAdminTranslation } from '../constants';
import {
  RequestSavedReports, ReceiveSavedReports, FailSavedReports,
  RequestSaveSavedReports, ReceiveSaveSavedReports, FailSaveSavedReports,
  ClearSavedReports
} from './action-types';
import { api } from './api';
import { DashboardComponentSavedReports } from './constants';

export const actionCreators = {

  ...commonActionCreators<SavedReportDashboard>(DashboardComponentSavedReports, endpoints, normalizeSequence),

  loadSavedReports: (dashboardTypeId: number, searchFavouriteId: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestSavedReports(dashboardTypeId));

      try {

        const [dashboard, savedReportsWire] = await Promise.all([
          api.loadDashboard(dashboardTypeId),
          api.loadSavedReports(dashboardTypeId)
        ]);

        const evaluationsWire = await commonApi.loadSavedReportEvaluations(endpoints,
          savedReportsWire.map(report => report.code?.v), dashboard?.accessType?.v);

        dispatch(new ReceiveSavedReports(
          dashboardTypeId,
          dashboard?.accessType?.v,
          dashboard?.code?.d,
          mapSavedReportsFromWire(savedReportsWire, evaluationsWire)
        ));

        const evaluationErrors = mapSavedReportsWarnings(evaluationsWire);

        if (Object.keys(evaluationErrors).length > 0) {
          actionCreators.updateSavedReportValidation(evaluationErrors)(dispatch, getState);
        }

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.dashboardAdministration,
            translationId: dashboardAdminTranslation.no_access_to_dashboard_admin
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute(dashboardTypeId, searchFavouriteId) });
        }

        dispatch(new FailSavedReports(dashboardTypeId));
      }
    },

  saveSavedReports: (
    dashboardTypeId: number,
    searchFavouriteId: number,
    savedReports: SavedReportDashboard[],
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestSaveSavedReports(dashboardTypeId));

      try {
        await api.saveSavedReports(dashboardTypeId, mapSavedReportsToWire(savedReports));

        dispatch(new ReceiveSaveSavedReports(dashboardTypeId));

        navigate.to({ pathname: getBackRoute(dashboardTypeId, searchFavouriteId) }, { state: { internal: true } });

        dispatch(new SuccessNotification(successMessage));
      } catch (err) {

        if ((err?.userMessages?.length ?? 0) > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));

          const { savedReport: code, ...validation } = err.validationErrors;

          actionCreators.updateSavedReportValidation(mapSavedReportsValidation({ code, ...validation }))(dispatch, getState);
        }

        dispatch(new FailSaveSavedReports(dashboardTypeId));

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute(dashboardTypeId, searchFavouriteId) }, { state: { internal: true } });
        }
      }
    },

  clearSavedReports: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearSavedReports());
    },

  displayError: (errorMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
